/**
 * Defaults and the palette catalogue for the Design Builder.
 *
 * The DVM widget entries mirror `options.layoutTypes` in
 * `assets/self-service-config/dvm-layout-builder.json`. They are listed here so
 * the palette renders instantly (with icons + sensible drop sizes) before the
 * config service resolves; `DesignBuilderComponent` merges any extra types the
 * JSON declares on top of this list, so adding a type to the config still
 * surfaces it here with no code change.
 */

import { environment } from '../../../../../my-app/src/environments/environment';

import type {
  AutoLayout,
  DesignCanvasSettings,
  DesignNodeStyle,
  IconContent,
  PaletteItem,
  TextContent,
} from './design-builder.types';

// ── Canvas ───────────────────────────────────────────────────────────────────

export const DEFAULT_CANVAS: DesignCanvasSettings = {
  width: 1440,
  height: 1024,
  background: '#f5f7fa',
  grid: { size: 8, snap: true, visible: true },
};

export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 4;
export const ZOOM_STEP = 1.15;

/** Snap threshold in SCREEN px, so it feels constant at every zoom level. */
export const SNAP_THRESHOLD_PX = 6;

/** Gap kept between the artboard and the viewport edge. Panning is clamped to
 *  this, so the page stays put instead of drifting off into empty space. */
export const CANVAS_EDGE_MARGIN = 24;

/** Smallest node the resize maths will produce. */
export const MIN_NODE_SIZE = 8;

/** Undo stack depth. */
export const HISTORY_LIMIT = 100;

/**
 * Commits with the same label inside this window collapse into one undo entry,
 * so a drag produces one history step rather than one per pointermove.
 */
export const HISTORY_COALESCE_MS = 400;

/** Offset applied to duplicated nodes so the copy is visible. */
export const DUPLICATE_OFFSET = 16;

// ── Node defaults ────────────────────────────────────────────────────────────

export const DEFAULT_STYLE: DesignNodeStyle = {
  fill: { kind: 'solid', color: '#ffffff' },
  stroke: null,
  radius: 0,
  shadow: null,
  blur: 0,
  overflow: 'visible',
};

export const DEFAULT_AUTO_LAYOUT: AutoLayout = {
  mode: 'none',
  gap: 16,
  padding: [16, 16, 16, 16],
  align: 'start',
  justify: 'start',
  wrap: false,
};

export const DEFAULT_TEXT: TextContent = {
  value: 'Text',
  fontFamily: 'Inter, Roboto, sans-serif',
  fontSize: 16,
  fontWeight: 400,
  fontStyle: 'normal',
  color: '#101828',
  align: 'left',
  verticalAlign: 'top',
  lineHeight: 1.4,
  letterSpacing: 0,
  textTransform: 'none',
  textDecoration: 'none',
};

export const DEFAULT_ICON: IconContent = {
  name: 'Star',
  color: '#344054',
  strokeWidth: 2,
  absoluteStrokeWidth: false,
  fill: 'none',
};

/** Default drop size for an icon — square, so the glyph is not distorted. */
export const DEFAULT_ICON_SIZE = 48;

/**
 * Input controls (dropdown, slider, input box, upload) are form controls, not
 * cards: they bring their own label and border. Wrapping them in card chrome
 * plus 12px padding both double-framed them and clipped the control, so they
 * start transparent, unpadded and unclipped — `overflow: visible` also lets a
 * dropdown panel escape the node box.
 */
export const DEFAULT_INPUT_WIDGET_STYLE: DesignNodeStyle = {
  fill: { kind: 'none' },
  stroke: null,
  radius: 0,
  shadow: null,
  padding: [0, 0, 0, 0],
  overflow: 'visible',
};

/**
 * The zone slider draws its own pill / badge / track but no panel, so the node
 * supplies the soft panel behind it (as in the reference design). Not
 * clipped: the thumb overhangs the track at both ends.
 */
export const DEFAULT_ZONE_SLIDER_STYLE: DesignNodeStyle = {
  fill: { kind: 'solid', color: '#f8fafc' },
  stroke: null,
  radius: 8,
  shadow: null,
  padding: [12, 14, 12, 14],
  overflow: 'visible',
};

/** Card chrome applied to freshly dropped widget nodes. */
export const DEFAULT_WIDGET_STYLE: DesignNodeStyle = {
  fill: { kind: 'solid', color: '#ffffff' },
  stroke: { color: '#e4e7ec', width: 1, style: 'solid' },
  radius: 12,
  shadow: { x: 0, y: 1, blur: 3, spread: 0, color: 'rgba(16, 24, 40, 0.08)' },
  padding: [12, 12, 12, 12],
  overflow: 'hidden',
};
export const SCREEN_FRAME_KEY = 'frame:screen';

/** Default (and minimum) height of the screen; it grows with the content. */
export const SCREEN_FRAME_HEIGHT = 720;

/** Space kept under the lowest element when a screen grows to its content. */
export const SCREEN_CONTENT_PADDING = 40;

// The screen is NOT a palette item. Every design starts with exactly one, so
// offering it as something to drag would only invite a second one nobody wants.
// It is created by `createEmptyDocument` instead.

const DESIGN_ITEMS: PaletteItem[] = [
  { key: 'rect', label: 'Rectangle', icon: 'rectangle', group: 'design', nodeType: 'RECT', defaultWidth: 200, defaultHeight: 120 },
  { key: 'ellipse', label: 'Ellipse', icon: 'circle', group: 'design', nodeType: 'ELLIPSE', defaultWidth: 160, defaultHeight: 160 },
  { key: 'line', label: 'Line', icon: 'horizontal_rule', group: 'design', nodeType: 'LINE', defaultWidth: 240, defaultHeight: 1 },
  { key: 'text', label: 'Text', icon: 'title', group: 'design', nodeType: 'TEXT', defaultWidth: 220, defaultHeight: 32 },
  { key: 'image', label: 'Image', icon: 'image', group: 'design', nodeType: 'IMAGE', defaultWidth: 240, defaultHeight: 160 },
];

/**
 * Icons + drop sizes per DVM layoutType. Charts get a wide default, KPIs a
 * card, input controls a single row — so a drop looks right before any config.
 */
export const WIDGET_PALETTE_META: Record<
  string,
  { icon: string; group: 'charts' | 'inputs'; width: number; height: number }
> = {
  WATERFALL: { icon: 'waterfall_chart', group: 'charts', width: 560, height: 340 },
  DRILL_DOWN_TABLE: { icon: 'account_tree', group: 'charts', width: 640, height: 380 },
  BAR: { icon: 'bar_chart', group: 'charts', width: 520, height: 320 },
  LINE_AREA: { icon: 'show_chart', group: 'charts', width: 520, height: 320 },
  SCATTER_BUBBLE: { icon: 'scatter_plot', group: 'charts', width: 520, height: 320 },
  PIE_COMPOSITION: { icon: 'pie_chart', group: 'charts', width: 440, height: 320 },
  TABLE_MATRIX: { icon: 'table_chart', group: 'charts', width: 640, height: 360 },
  NESTED_TABLE: { icon: 'table_rows', group: 'charts', width: 1100, height: 620 },
  HORIZONTAL_BAR_CHART: { icon: 'align_horizontal_left', group: 'charts', width: 520, height: 320 },
  KPI: { icon: 'speed', group: 'charts', width: 280, height: 140 },
  // Input controls stack a label over the control itself, so they need more
  // height than the control alone — at 80px the label pushed the input out of
  // the clipped box and it rendered half cut.
  INPUT_BOX: { icon: 'input', group: 'inputs', width: 280, height: 104 },
  SLIDER: { icon: 'tune', group: 'inputs', width: 320, height: 110 },
  // Value pill + badge row, the zoned track and the min/max labels, plus the
  // node's own panel padding.
  ZONE_SLIDER: { icon: 'linear_scale', group: 'inputs', width: 680, height: 112 },
  DROPDOWN: { icon: 'arrow_drop_down_circle', group: 'inputs', width: 280, height: 104 },
  FILE_UPLOAD: { icon: 'upload_file', group: 'inputs', width: 280, height: 112 },
  POPUP: { icon: 'open_in_new', group: 'inputs', width: 220, height: 72 },
  // The button plus a one-line result message under it.
  BUTTON: { icon: 'smart_button', group: 'inputs', width: 180, height: 56 },
};

const FALLBACK_WIDGET_META = { icon: 'widgets', group: 'charts' as const, width: 480, height: 300 };

/** Widget palette entries, in the order the config lists the layout types. */
export function widgetPaletteItems(
  layoutTypes: ReadonlyArray<{ label: string; value: string }>,
): PaletteItem[] {
  return layoutTypes.map((type) => {
    const meta = WIDGET_PALETTE_META[type.value] ?? FALLBACK_WIDGET_META;
    return {
      key: `widget:${type.value}`,
      label: type.label,
      icon: meta.icon,
      group: meta.group,
      nodeType: 'WIDGET',
      layoutType: type.value,
      defaultWidth: meta.width,
      defaultHeight: meta.height,
    } satisfies PaletteItem;
  });
}

/** The full palette: design primitives first, then the config-driven widgets. */
export function buildPalette(
  layoutTypes: ReadonlyArray<{ label: string; value: string }>,
): PaletteItem[] {
  const known = new Set(layoutTypes.map((type) => type.value));
  const extras = DESIGN_ONLY_LAYOUT_TYPES.filter((type) => !known.has(type.value));
  return [...DESIGN_ITEMS, ...widgetPaletteItems([...layoutTypes, ...extras])];
}

/**
 * Widgets the Design Builder renders that the DVM Layout Builder has no build
 * flow for yet. Kept out of the shared `layoutTypes` config so they never show
 * up in that builder's chart-type picker.
 */

// Nested Table is NOT listed: it is a Table type of Table & Matrix (pick
// "Nested Table" in that widget's Table type), so it has no palette entry.
export const DESIGN_ONLY_LAYOUT_TYPES: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Zone Slider', value: 'ZONE_SLIDER' },
  { label: 'Button', value: 'BUTTON' },
];

/** Used until the config service resolves. */
export const FALLBACK_LAYOUT_TYPES: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Waterfall', value: 'WATERFALL' },
  { label: 'Drill-Down Table', value: 'DRILL_DOWN_TABLE' },
  { label: 'Bar Charts', value: 'BAR' },
  { label: 'Line & Area', value: 'LINE_AREA' },
  { label: 'Scatter & Bubble', value: 'SCATTER_BUBBLE' },
  { label: 'Pie & Composition', value: 'PIE_COMPOSITION' },
  { label: 'Table & Matrix', value: 'TABLE_MATRIX' },
  { label: 'Kpi Card', value: 'KPI' },
  { label: 'Input Box', value: 'INPUT_BOX' },
  { label: 'Slider', value: 'SLIDER' },
  { label: 'Dropdown', value: 'DROPDOWN' },
  { label: 'File Upload', value: 'FILE_UPLOAD' },
  { label: 'Popup', value: 'POPUP' },
];

export const PALETTE_GROUP_LABELS: Record<string, string> = {
  design: 'Design',
  charts: 'Charts & tables',
  inputs: 'Inputs & controls',
};

// ── Persistence ──────────────────────────────────────────────────────────────

export const DESIGNS_STORAGE_KEY = 'dvm.selfService.designs';
export const DESIGN_DRAFT_STORAGE_KEY = 'dvm.selfService.designDraft';
export const DESIGNS_API_PATH = '/api/dvm/self-service/designs';
/** Projects — the parent that groups several dashboards. */
export const PROJECTS_API_PATH = '/api/dvm/self-service/design-projects';
export const DESIGN_PROJECTS_STORAGE_KEY = 'dvm.selfService.designProjects';
/**
 * Shape Builder backend. Each Angular build resolves it from that build's
 * primary platform URL.
 */
export const SHAPE_BUILDER_BASE_URL = String(environment.URL || '').replace(/\/+$/, '');
export const DESIGN_SAVE_API_URL = `${SHAPE_BUILDER_BASE_URL}/api/shape-builder-components`;
/** Draft autosave debounce. */
export const DRAFT_SAVE_DEBOUNCE_MS = 800;

/** Widgets an Approval button can read rows from (one { uuid, value } item per row). */
export const APPROVAL_TABLE_TYPES: ReadonlySet<string> = new Set(['NESTED_TABLE', 'TABLE_MATRIX']);
